import { ReactNode } from 'react';
import { Card } from '@/components/ui/Card.tsx';
import HeadlineNumber from '@/components/ui/HeadlineNumber.tsx';
import SegmentedBar, { BarSegment } from '@/components/ui/SegmentedBar.tsx';
import { ColumnLabel } from '@/components/ui/Title.tsx';
import { cn } from '@/lib/utils.ts';

export interface MetricCardFooterRow {
  id: string;
  label: ReactNode;
  value: ReactNode;
}

interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  /** Główna liczba karty — już sformatowana, karta nie zna walut ani jednostek. */
  value: ReactNode;
  /** Mianownik obok wartości: „z 22 miejsc”, „z 15 400,00 zł”. */
  unit?: ReactNode;
  /** Udział wyrażony napisem („59%”); stoi przy prawej krawędzi. */
  share?: ReactNode;
  segments?: BarSegment[];
  /** Wiersze pod kreską — dopowiedzenia do liczby głównej. */
  footer?: MetricCardFooterRow[];
  /** Karta bez treści — liczba i udział gasną, bo nie ma czego pokazywać. */
  dim?: boolean;
}

/**
 * Kafel jednej wielkości: obłożenie, wpłaty, budżet. Duża liczba, mianownik,
 * udział, pasek i stopka z dopowiedzeniami.
 *
 * Wszystko przychodzi sformatowane — karta nie liczy procentów ani nie zna
 * `AvailabilityFieldDto`; od tego jest `availabilityUtils`.
 */
const MetricCard = ({
  title,
  value,
  unit,
  share,
  segments,
  footer,
  dim = false,
  className,
  ...props
}: MetricCardProps) => (
  <Card className={cn('px-5 py-4', className)} {...props}>
    <ColumnLabel>{title}</ColumnLabel>

    <div className="mt-2.5 flex items-baseline gap-2">
      <HeadlineNumber dimmed={dim}>{value}</HeadlineNumber>
      {unit && (
        <span className="text-base font-semibold text-neutral-500">{unit}</span>
      )}
      {share && (
        <span
          className={cn(
            'ml-auto text-sm font-bold tabular-nums',
            dim ? 'text-neutral-400' : 'text-neutral-700',
          )}
        >
          {share}
        </span>
      )}
    </div>

    {segments && <SegmentedBar className="mt-3" segments={segments} />}

    {footer && footer.length > 0 && (
      <div className="mt-3.5 flex flex-col gap-2 border-t border-neutral-100 pt-3">
        {footer.map((row) => (
          <div
            key={row.id}
            className="flex items-baseline justify-between gap-3"
          >
            <span className="text-sm text-neutral-500">{row.label}</span>
            <span className="text-[15px] font-bold tabular-nums text-ink">
              {row.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </Card>
);

export default MetricCard;
